import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';
import { PriceDisclosure } from './elements/PriceDisclosure';
import { Currency, FieldPrice, PricePerHour } from '../../../types';
import { getWeekDayFromNumber } from '../../../utils/language';

type Props = {
  openingHours: string;
  closingHours: string;
  currency: Currency;
  prices: FieldPrice[];
  setPrices: React.Dispatch<React.SetStateAction<FieldPrice[]>>;
};

const PriceIntervalsForm = ({
  openingHours,
  closingHours,
  currency,
  prices,
  setPrices,
}: Props) => {
  const { t } = useTranslation();

  const mondayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 1)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const tuesdayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 2)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const wednesdayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 3)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const thursdayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 4)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const fridayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 5)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const saturdayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 6)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const sundayPrices = useMemo(
    () =>
      prices
        .filter((p) => p.day === 0)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [prices]
  );

  const handlePriceAdd = useCallback(
    (
      startTime: string,
      endTime: string,
      price: PricePerHour,
      days: number[]
    ) => {
      if (days.length === 1) {
        const existingPriceInterval = prices.findIndex(
          (p) =>
            p.startTime === startTime &&
            p.endTime === endTime &&
            p.day === days[0]
        );

        const overlappingPriceInterval = prices.find(
          (p) =>
            p.startTime < endTime && p.endTime > startTime && p.day === days[0]
        );

        if (existingPriceInterval !== -1) {
          setPrices((prev) => {
            prev[existingPriceInterval].priceId = price.id;
            prev[existingPriceInterval].price = price.value;
            return [...prev];
          });
          return;
        }

        if (overlappingPriceInterval) {
          toast.error(
            t(COMMON_TRANSLATE_KEYS.OVERLAPPING_PRICE_INTERVAL, {
              day: getWeekDayFromNumber(days[0]).toLowerCase(),
            })
          );
          return;
        }

        setPrices([
          ...prices,
          {
            day: days[0],
            startTime,
            endTime,
            price: price.value,
            priceId: price.id,
            currency,
          },
        ]);
      } else if (days.length > 1) {
        const overlappingPriceIntervals = prices.filter(
          (p) =>
            p.startTime < endTime &&
            p.endTime > startTime &&
            days.includes(p.day)
        );

        let existingPriceIntervalIndexes: number[] = [];

        prices.forEach((p, i) => {
          if (
            p.startTime === startTime &&
            p.endTime === endTime &&
            days.includes(p.day)
          ) {
            existingPriceIntervalIndexes.push(i);
          }
        });

        if (existingPriceIntervalIndexes.length > 0) {
          setPrices((prev) => {
            existingPriceIntervalIndexes.forEach((i) => {
              prev[i].priceId = price.id;
              prev[i].price = price.value;
            });
            return [...prev];
          });
          return;
        }

        if (overlappingPriceIntervals.length > 0) {
          toast.error(
            t(COMMON_TRANSLATE_KEYS.OVERLAPPING_PRICE_MULTIPLE_INTERVALS, {
              days: _.uniq(
                overlappingPriceIntervals.map((p) =>
                  getWeekDayFromNumber(p.day).toLowerCase()
                )
              ).join(', '),
            })
          );
          return;
        }

        setPrices([
          ...prices,
          ...days.map((day) => ({
            day,
            startTime,
            endTime,
            priceId: price.id,
            price: price.value,
            currency,
          })),
        ]);
      }
    },
    [currency, prices, setPrices, t]
  );

  const handlePriceRemove = useCallback(
    (startTime: string, endTime: string, day: number) => {
      setPrices(
        prices.filter(
          (p) =>
            !(
              p.startTime === startTime &&
              p.endTime === endTime &&
              p.day === day
            )
        )
      );
    },
    [prices, setPrices]
  );

  return (
    <>
      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.MONDAY_NORMAL)} (${
          mondayPrices.length
        })`}
        prices={mondayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={1}
        defaultOpen
      />

      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.TUESDAY_NORMAL)} (${
          tuesdayPrices.length
        })`}
        prices={tuesdayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={2}
        defaultOpen={tuesdayPrices.length > 0}
      />

      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.WEDNESDAY_NORMAL)} (${
          wednesdayPrices.length
        })`}
        prices={wednesdayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={3}
        defaultOpen={wednesdayPrices.length > 0}
      />

      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.THURSDAY_NORMAL)} (${
          thursdayPrices.length
        })`}
        prices={thursdayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={4}
        defaultOpen={thursdayPrices.length > 0}
      />

      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.FRIDAY_NORMAL)} (${
          fridayPrices.length
        })`}
        prices={fridayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={5}
        defaultOpen={fridayPrices.length > 0}
      />

      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.SATURDAY_NORMAL)} (${
          saturdayPrices.length
        })`}
        prices={saturdayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={6}
        defaultOpen={saturdayPrices.length > 0}
      />

      <PriceDisclosure
        title={`${t(COMMON_TRANSLATE_KEYS.SUNDAY_NORMAL)} (${
          sundayPrices.length
        })`}
        prices={sundayPrices}
        onAddPrice={handlePriceAdd}
        onRemovePrice={handlePriceRemove}
        complexClosingHours={closingHours}
        complexOpeningHours={openingHours}
        currency={currency}
        day={0}
        defaultOpen={sundayPrices.length > 0}
      />
    </>
  );
};

export default PriceIntervalsForm;
